/**
 * LARX API Service
 *
 * Service for interacting with the LARX (degree-reqs-api) backend.
 * Used for fetching universities, courses, programs, equivalencies, and requirements.
 */

import axios, { AxiosInstance } from 'axios'

/** Course from LARX API */
export interface ApiCourse {
  id: number
  name: string
  prefix?: string
  num?: string
  credit_hours: number
  university_id: number
}

/** Course equivalency relationship */
export interface CourseEquivalency {
  id: number
  source_courses: ApiCourse[]
  target_courses: ApiCourse[]
  accepting_university_id: number
  status: string
}

/** Program from LARX API */
export interface ApiProgram {
  id: number
  name: string
  description?: string
  degree_type: string
  system_type: string
  CIP?: string | null
  external_id: string
  academic_year?: string | null
  academic_term?: string | null
  root_requirement_set_id: number
  university_id: number
  curriculums_ids: number[]
}

/** University from LARX API */
export interface ApiUniversity {
  id: number
  name: string
  abbreviation?: string
  state?: string
}

class DegreeReqsApiService {
  private client: AxiosInstance

  constructor() {
    const apiUrl = import.meta.env.VITE_DEGREE_REQS_API_URL || 'http://localhost:8000'

    this.client = axios.create({
      baseURL: apiUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    })
  }

  /**
   * Find a university by name and return its ID
   */
  async findUniversityByName(name: string): Promise<number | null> {
    try {
      const response = await this.client.get('/universities', {
        params: { search: name },
      })
      const universities = response.data as ApiUniversity[]
      const university = universities.find((u) => u.name === name)
      return university ? university.id : null
    } catch (error) {
      console.error(`Error finding university ${name}:`, error)
      return null
    }
  }

  /**
   * Get a university by ID
   */
  async getUniversityById(id: number): Promise<ApiUniversity | null> {
    try {
      const response = await this.client.get(`/universities/${id}`)
      return response.data as ApiUniversity
    } catch (error) {
      console.error(`Error getting university ${id}:`, error)
      return null
    }
  }

  /**
   * Resolve a university ID from config (uses ID if provided, otherwise looks up by name)
   */
  async resolveUniversityId(config: { id?: number; name: string }): Promise<number | null> {
    if (config.id) {
      return config.id
    }
    return this.findUniversityByName(config.name)
  }

  /**
   * Lookup a course by prefix and num to get its database ID
   */
  async getCourseByPrefixAndNum(
    universityId: number,
    prefix: string,
    num: string
  ): Promise<ApiCourse | null> {
    try {
      const response = await this.client.post('/courses/search', {
        university_id: universityId,
        prefix: prefix,
        num: num,
      })

      const courses = response.data as ApiCourse[]

      // Find exact match
      const course = courses.find(
        (c) => c.prefix?.toLowerCase() === prefix.toLowerCase() && c.num === num
      )

      return course || null
    } catch (error) {
      console.error(`Error looking up course ${prefix} ${num}:`, error)
      return null
    }
  }

  /**
   * Get course equivalencies for a specific course ID
   */
  async getCourseEquivalencies(
    courseId: number,
    targetUniversityId: number
  ): Promise<CourseEquivalency[]> {
    try {
      const response = await this.client.get(`/courses/${courseId}/equivalencies`, {
        params: { target_university_id: targetUniversityId },
      })
      return response.data as CourseEquivalency[]
    } catch (error) {
      console.error(`Error getting equivalencies for course ${courseId}:`, error)
      return []
    }
  }

  /**
   * Get all programs for a university
   */
  async getUniversityPrograms(universityId: number): Promise<ApiProgram[]> {
    try {
      const response = await this.client.get(`/university/${universityId}/programs/`)
      return response.data as ApiProgram[]
    } catch (error) {
      console.error(`Error getting programs for university ${universityId}:`, error)
      return []
    }
  }

  /**
   * Get program details including root_requirement_set_id
   */
  async getProgram(programId: number): Promise<ApiProgram | null> {
    try {
      const response = await this.client.get(`/programs/${programId}`)
      return response.data as ApiProgram
    } catch (error) {
      console.error(`Error getting program ${programId}:`, error)
      return null
    }
  }

  /**
   * Get program requirements from LARX using the root_requirement_set_id
   */
  async getProgramRequirements(programId: number): Promise<any> {
    try {
      // First get the program to find its root_requirement_set_id
      const program = await this.getProgram(programId)
      if (!program || !program.root_requirement_set_id) {
        console.warn(`Program ${programId} not found or has no root requirement set`)
        return null
      }

      // Now fetch the root requirement set
      const response = await this.client.get(`/requirement_set/${program.root_requirement_set_id}`)
      return response.data
    } catch (error) {
      console.error(`Error getting requirements for program ${programId}:`, error)
      return null
    }
  }
}

export const degreeReqsApi = new DegreeReqsApiService()
export default DegreeReqsApiService
